/**
 * AIQuickReview - Simplified AI Quick Review screen focused on document approval workflow
 * Removes all statistics cards and keeps only document management functionality
 */

import React, { useState } from 'react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import DocumentManagementPanel from '../DocumentManagementPanel';
import UploadPreviewSection from './UploadPreviewSection';

const AIQuickReview: React.FC = () => {
  const { 
    setCurrentScreen, 
    documentStats,
    allUploads,
    setAllUploads,
    fetchDocumentStatistics,
    selectedClient
  } = useOnboarding();
  
  const [showDocumentManagement, setShowDocumentManagement] = useState(false);

  // Handle document management actions
  const handleDocumentAction = (uploadId: string, action: 'approved' | 'rejected' | 'removed') => {
    // Update local upload list to reflect the action
    setAllUploads(allUploads.map(upload => {
      if (upload.upload_id === uploadId) {
        if (action === 'removed') {
          return null; // Will be filtered out
        }
        return {
          ...upload,
          upload_status: action === 'approved' ? 'ingesting' : 'rejected'
        };
      }
      return upload;
    }).filter(Boolean) as any[]);
    
    // Refresh document statistics
    fetchDocumentStatistics();
  };

  const readyUploads = allUploads.filter(upload => 
    ['ready', 'ingesting', 'complete'].includes(upload.upload_status)
  );

  return (
    <div className="min-h-screen bg-kielis-glacier">
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="container mx-auto px-5">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-kielis-navy font-semibold">Document Review</h3>
              <p className="text-gray-600 text-sm">Review and approve documents before importing</p>
            </div>
            <div className="flex items-center gap-4">
              {documentStats.pendingApproval > 0 && (
                <button
                  onClick={() => setShowDocumentManagement(true)}
                  className="bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-700 flex items-center gap-2"
                >
                  📋 Manage Documents ({documentStats.pendingApproval})
                </button>
              )}
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((step, index) => (
                  <div 
                    key={step}
                    className={`w-2 h-2 rounded-full ${
                      index < 3 ? 'bg-kielis-success' : index === 3 ? 'bg-kielis-gold' : 'bg-gray-300'
                    }`} 
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-5 py-8">
        {/* Document Approval Workflow Card */}
        {(documentStats.totalDocuments > 0 || documentStats.pendingApproval > 0) && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6">
            <h4 className="text-kielis-navy font-medium mb-4">📋 Document Approval Workflow</h4>
            <div className="space-y-4">
              {/* Workflow Steps */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <span className="font-medium">Upload & Process</span>
                  </div>
                  <div className="text-2xl">→</div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <span className="font-medium">Review & Approve</span>
                  </div>
                  <div className="text-2xl">→</div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <span className="font-medium">Import Complete</span>
                  </div>
                </div>
              </div>
              
              {/* Current Status Counts */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {documentStats.totalDocuments - documentStats.pendingApproval - documentStats.approvedDocuments}
                  </div>
                  <div className="text-sm text-blue-700">Processing</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{documentStats.pendingApproval}</div>
                  <div className="text-sm text-yellow-700">Pending Review</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{documentStats.approvedDocuments}</div>
                  <div className="text-sm text-green-700">Completed</div>
                </div>
              </div>
              
              {/* Quick Actions */}
              {documentStats.pendingApproval > 0 && (
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setShowDocumentManagement(true)}
                    className="bg-kielis-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-opacity-90"
                  >
                    Review {documentStats.pendingApproval} Documents
                  </button>
                  <div className="text-sm text-gray-600 px-3 py-2">
                    {documentStats.qualityScore > 0 && (
                      <span>Average quality: {documentStats.qualityScore.toFixed(0)}%</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload Preview Section */}
        <UploadPreviewSection uploads={readyUploads} />

        {/* Ready to Complete Setup */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
          <h4 className="text-green-800 font-medium mb-2">🎉 Ready to Complete Setup!</h4>
          <p className="text-green-700 mb-4">
            Your documents are being processed. You can continue to the dashboard while background imports complete.
          </p>
          <div className="flex gap-6 text-sm text-green-700">
            <span>✅ Documents uploaded</span>
            <span>✅ Review process active</span>
            <span>✅ Ready for next step</span>
          </div>
        </div>

        <div className="text-center">
          <button 
            className="bg-kielis-navy text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-opacity-90 transition-all"
            onClick={() => setCurrentScreen('completeness')}
          >
            Complete Setup
          </button>
        </div>
      </div>

      {/* Document Management Panel */}
      <DocumentManagementPanel
        isOpen={showDocumentManagement}
        onClose={() => setShowDocumentManagement(false)}
        onDocumentAction={handleDocumentAction}
        clientId={selectedClient?.id}
      />
    </div>
  );
};

export default AIQuickReview;